import React, { createContext, useMemo } from "react";
import { useQuery } from "react-query";
import { USERS, ROLES } from "../config/apiConfig";
import {
  canViewAllData,
  hasPermission,
  isPlatformStaff,
} from "../utils/permissions";
import axios from "../config/axiosInstance";

const ApplicationContext = createContext();

const PermissionProvider = ({ children }) => {
  const { data: user, refetch } = useQuery(["user"], async () => {
    try {
      const { data } = await axios.get(`${USERS.GET_USER_CURRENT}`);

      return data.data;
    } catch (error) {
      return "USER";
    }
  });

  // Permissões efetivas do usuário (RBAC). Vem separado de /user porque é
  // resolvido no servidor a cada requisição — papel revogado deixa de valer
  // sem esperar o token vencer. Ver src/security/accessControl.js no backend.
  //
  // `access` indefinido significa "ainda não sei", e o hasPermission trata
  // isso como permitido: piscar o menu a cada carregamento é pior do que
  // mostrar um item que o backend vai recusar.
  const { data: access, refetch: refetchAccess } = useQuery(
    ["me-access"],
    async () => {
      const { data } = await axios.get(ROLES.MY_ACCESS);
      return data.data;
    },
    {
      staleTime: 60_000,
      // Backend antigo (sem a rota) não pode quebrar a aplicação inteira.
      retry: false,
      onError: () => null,
    },
  );

  const value = useMemo(
    () => ({
      user,
      refetchUser: refetch,
      access,
      refetchAccess,
      can: (permissionKey) => hasPermission(access, permissionKey),
      // Chave `data.viewAll`: vê os dados de toda a empresa (não só os próprios).
      // Diferente de `can`, "ainda não sei" vale não (ver utils/permissions.js).
      canViewAll: canViewAllData(access),
      // O contrário, já com as permissões carregadas: só vê os próprios dados.
      // Telas que FORÇAM um filtro por usuário usam este (não `!canViewAll`),
      // para não restringir quem ainda está carregando.
      onlyOwnData: Boolean(access) && !canViewAllData(access),
      isPlatformStaff: isPlatformStaff(access),
    }),
    [user, refetch, access, refetchAccess],
  );

  return (
    <ApplicationContext.Provider value={value}>
      {children}
    </ApplicationContext.Provider>
  );
};

const useApplication = () => React.useContext(ApplicationContext);

export { PermissionProvider, useApplication };
